/**
 * Geolocation utility for detecting user's country based on IP
 */

import { GEOLOCATION_FALLBACK_API_URL } from '@/config';

const GEOLOCATION_CACHE_KEY = 'lema_user_country';
const GEOLOCATION_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

interface GeolocationData {
  country: string;
  timestamp: number;
}

/**
 * Detect user's country code from IP address
 * Uses multiple fallback methods:
 * 1. Check localStorage cache
 * 2. Try Vercel geolocation headers (server-side)
 * 3. Fallback to ipapi.co (client-side)
 */
export async function detectUserCountry(): Promise<string | null> {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return null;
  }

  // Check cache first
  const cached = getCachedCountry();
  if (cached) {
    return cached;
  }

  try {
    // Try to get country from our own API endpoint (which uses Vercel headers)
    const response = await fetch('/api/geolocation', {
      method: 'GET',
      cache: 'no-store',
    });

    if (response.ok) {
      const data = await response.json();
      if (data.country) {
        cacheCountry(data.country);
        return data.country;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch geolocation from API:', error);
  }

  try {
    // Fallback to ipapi.co (free tier: 1000 requests/day)
    const response = await fetch(GEOLOCATION_FALLBACK_API_URL, {
      method: 'GET',
      cache: 'no-store',
    });

    if (response.ok) {
      const data = await response.json();
      if (data.country_code) {
        cacheCountry(data.country_code);
        return data.country_code;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch geolocation from ipapi.co:', error);
  }

  return null;
}

/**
 * Get cached country code from localStorage
 */
function getCachedCountry(): string | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const cached = localStorage.getItem(GEOLOCATION_CACHE_KEY);
    if (!cached) {
      return null;
    }

    const data: GeolocationData = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - data.timestamp < GEOLOCATION_CACHE_DURATION) {
      return data.country;
    }

    // Cache expired, remove it
    localStorage.removeItem(GEOLOCATION_CACHE_KEY);
    return null;
  } catch (error) {
    console.warn('Error reading geolocation cache:', error);
    return null;
  }
}

/**
 * Cache country code in localStorage
 */
function cacheCountry(country: string): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const data: GeolocationData = {
      country,
      timestamp: Date.now(),
    };
    localStorage.setItem(GEOLOCATION_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Error caching geolocation:', error);
  }
}

/**
 * Map country codes to language codes
 * Future country-language mappings can be easily added here
 */
const COUNTRY_TO_LANGUAGE_MAP: Record<string, string> = {
  ID: 'id', // Indonesia -> Indonesian
  CN: 'zh-CN', // China -> Simplified Chinese
  // Add more country-to-language mappings here as needed
};

const DEFAULT_LANGUAGE = 'en';

/**
 * Map country code to language code
 * ID for Indonesia, EN for everything else
 */
export function countryToLanguage(countryCode: string | null): string {
  if (!countryCode) {
    return DEFAULT_LANGUAGE;
  }

  const normalized = countryCode.toUpperCase();
  return COUNTRY_TO_LANGUAGE_MAP[normalized] || DEFAULT_LANGUAGE;
}

/**
 * Detect and return appropriate language based on user's location
 */
export async function detectLanguageFromLocation(): Promise<string> {
  const country = await detectUserCountry();
  return countryToLanguage(country);
}
